import {useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {useMutation, useQuery} from '@apollo/client';
import {Alert, Pagination, Spin, Switch, Table, Tooltip} from 'antd';
import moment from 'moment';
import {toast} from 'react-toastify';
import {Button} from '@dabafinance/react-components';
import shell from 'pages/AssetManagement/v2/pageShell.module.scss';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';
import {GET_ALL_STOCK_CLOSURES} from 'graphql/queries/stocks';
import {
  DELETE_STOCK_CLOSURE,
  SET_STOCK_CLOSURE_STATUS,
} from 'graphql/mutations/stocks';
import {NavTabsEnum} from 'utils/constants';
import {makeSelectTabOperations} from 'redux/store/auth';
import {ExchangeScopeSelector} from './ExchangeScopeSelector';
import {useExchangeScope} from './useExchangeScope';
import {ExchangeClosureModal} from './ExchangeClosureModal';
import {GET_EXCHANGE_TRADING_CONFIG} from './resolvers';
import styles from './exchanges.module.scss';

/**
 * Trading View → Exchange Trading Config → Closure windows.
 *
 * The dates an exchange is shut. Its own screen rather than a tab, because a
 * closure is a list of scheduled records with their own create/edit/delete
 * lifecycle — nothing like the single settings form beside it.
 *
 * It used to be a child route of the legacy "Update Stock Exchange" shell, so
 * opening it swapped the whole page for a screen headed "Update Stock
 * Exchange", offering a General Information tab that edits fields this app
 * moved elsewhere, and a back arrow that landed on the old stock listings
 * table. The closures themselves were always scoped to one exchange by id —
 * only the chrome around them belonged to something else.
 *
 * Scoping now comes from the shared exchange lens, so this reads the same
 * `?exchange=` as Trading Config and switching exchanges never leaves.
 */

/** Spelled out, never the two-letter code — the code reads as a country. */
const LANGUAGE_LABELS = {EN: 'English', FR: 'French'};

/** A closure is only meaningful relative to now, so rows are grouped by that. */
const isUpcoming = closure =>
  closure?.date ? moment(closure.date).isSameOrAfter(moment(), 'day') : false;

export const ExchangeClosures = () => {
  const navigate = useNavigate();
  const scope = useExchangeScope();
  const {exchangeId, activeExchange} = scope;

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const pageOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.StockListings),
  );

  const {data, loading, refetch} = useQuery(GET_ALL_STOCK_CLOSURES, {
    variables: {stockExchangeId: exchangeId, limit, page},
    skip: !exchangeId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
  });

  // Whether the market is open right now is the thing these records drive, so
  // it belongs on this screen rather than only on the settings form.
  // cache-and-network, and refetched with the list: activating a closure
  // changes this answer, and a cached one kept reporting "Closed" after the
  // record that closed the market had been deleted.
  const {data: configData, refetch: refetchMarket} = useQuery(
    GET_EXCHANGE_TRADING_CONFIG,
    {
      variables: {id: [exchangeId]},
      skip: !exchangeId,
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    },
  );
  const marketOpen = (configData?.getExchanges?.data ?? []).find(
    entry => entry.exchange?.id === exchangeId,
  )?.exchange?.closure?.isMarketOpen;

  const [deleteClosure, {loading: deleteLoading}] = useMutation(
    DELETE_STOCK_CLOSURE,
    {errorPolicy: 'all'},
  );
  const [setStatus] = useMutation(SET_STOCK_CLOSURE_STATUS, {
    errorPolicy: 'all',
  });

  const result = data?.getStockExchangeClosures;
  const unionError = result?.__typename === 'Error' ? result : null;
  const rows = unionError ? [] : result?.closures ?? [];
  const total = unionError ? 0 : result?.pagination?.total ?? 0;

  // The list arrives in insertion order, which put a closure from 2024 above
  // one next week. Soonest upcoming first, then past ones newest-first.
  const sorted = useMemo(() => {
    const upcoming = rows.filter(isUpcoming);
    const past = rows.filter(row => !isUpcoming(row));
    upcoming.sort(
      (a, b) => moment(a.date).valueOf() - moment(b.date).valueOf(),
    );
    past.sort((a, b) => moment(b.date).valueOf() - moment(a.date).valueOf());
    return upcoming.concat(past);
  }, [rows]);

  const upcomingCount = sorted.filter(isUpcoming).length;
  const activeCount = sorted.filter(row => row.isActive).length;

  const handleDelete = () => {
    deleteClosure({variables: {deleteStockExchangeClosureId: deleting?.id}})
      .then(({data: payload}) => {
        const outcome = payload?.deleteStockExchangeClosure;
        // The old screen destructured this one level too high, so __typename
        // was always undefined and a rejected delete reported success.
        if (outcome?.__typename === 'Error')
          return toast.error(outcome.message);
        toast.success(`"${deleting?.title}" deleted`);
        setDeleting(null);
        refetch();
        refetchMarket();
      })
      .catch(error => toast.error(error?.message));
  };

  const handleToggle = (closure, next) => {
    setTogglingId(closure.id);
    setStatus({
      variables: {
        setStockExchangeClosureStatusId: closure.id,
        status: next ? 'ACTIVE' : 'INACTIVE',
      },
    })
      .then(({data: payload}) => {
        const outcome = payload?.setStockExchangeClosureStatus;
        if (outcome?.__typename === 'Error')
          return toast.error(outcome.message);
        toast.success(next ? 'Closure activated' : 'Closure deactivated');
        refetch();
        refetchMarket();
      })
      .catch(error => toast.error(error?.message))
      .finally(() => setTogglingId(null));
  };

  const columns = [
    {
      title: 'Closure',
      dataIndex: 'title',
      render: (text, record) => (
        <div className={styles.nameCell}>
          <span className={styles.primaryText}>{record.title}</span>
          <span className={styles.mutedText}>{record.closureMessage}</span>
        </div>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'date',
      width: 210,
      render: (text, record) => {
        const when = moment(record.date);
        const upcoming = isUpcoming(record);
        return (
          <div className={styles.nameCell}>
            <span className={styles.primaryText}>
              {when.format('ddd D MMM YYYY')}
            </span>
            <span className={upcoming ? styles.upcomingText : styles.mutedText}>
              {when.format('HH:mm')} · {when.fromNow()}
            </span>
          </div>
        );
      },
    },
    {
      title: 'Languages',
      dataIndex: 'language',
      width: 170,
      render: (language, record) => {
        // Base language plus every translation. A closure written in English
        // and translated to French is served in both, so showing only the
        // base one understated the coverage.
        const all = [language].concat(
          (record.multiLang ?? [])
            .map(entry => entry.language)
            .filter(code => code && code !== language),
        );
        return (
          <div className={styles.chips}>
            {all.map(code => (
              <span key={code} className={styles.chip}>
                {LANGUAGE_LABELS[code] ?? code}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      title: 'Active',
      dataIndex: 'isActive',
      width: 110,
      render: (isActive, record) => {
        // The gateway refuses to activate a closure whose date has passed
        // ("Cannot activate a Stock Exchange closure with a date lesser than
        // the current date"). Better to say so on the control than to let
        // someone click and read it back as a red toast.
        const expired = !isUpcoming(record) && !isActive;
        return (
          <Tooltip
            title={
              expired
                ? 'This date has passed. A closure can only be activated while it is still ahead.'
                : isActive
                ? 'Investors see this closure. Turn it off to keep the record without applying it.'
                : 'Saved but not applied — investors are not told the market is shut on this date.'
            }>
            {/* A disabled antd Switch swallows pointer events, so the tooltip
                needs its own element to hang off. */}
            <span>
              <Switch
                size="small"
                checked={Boolean(isActive)}
                loading={togglingId === record.id}
                disabled={!pageOperations?.update || expired}
                onChange={next => handleToggle(record, next)}
              />
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: '',
      key: 'actions',
      width: 90,
      render: (text, record) => (
        // Was one "…" dropdown hiding edit, status and delete behind a click.
        <div
          className={styles.rowActions}
          onClick={event => event.stopPropagation()}>
          <Tooltip title={pageOperations?.update ? 'Edit' : 'No access'}>
            <button
              type="button"
              className={styles.iconButton}
              disabled={!pageOperations?.update}
              onClick={() => setEditing(record)}>
              <PencilIcon />
            </button>
          </Tooltip>
          <Tooltip
            title={
              !pageOperations?.delete
                ? 'No access'
                : record.isActive
                ? 'An active closure cannot be deleted. Turn it off first.'
                : 'Delete'
            }>
            {/* A disabled button swallows pointer events, so the tooltip needs
                its own element to hang off. */}
            <span>
              <button
                type="button"
                className={`${styles.iconButton} ${styles.iconButtonDanger}`}
                disabled={!pageOperations?.delete || record.isActive}
                onClick={() => setDeleting(record)}>
                <TrashIcon />
              </button>
            </span>
          </Tooltip>
        </div>
      ),
    },
  ];

  const header = (
    <div className={shell.head}>
      <div className={shell.headTop}>
        <div>
          <button
            type="button"
            className={shell.back}
            onClick={() =>
              navigate(
                exchangeId
                  ? `/dashboard/stocks/exchange-config?exchange=${exchangeId}`
                  : '/dashboard/stocks/exchange-config',
              )
            }>
            ← Trading config
          </button>
          <h1 className={shell.title}>Closure windows</h1>
          <div className={shell.sub}>
            Dates {activeExchange?.code ?? 'this exchange'} is shut. Orders
            placed on a closed day are held and processed the next session.
          </div>
        </div>
        {exchangeId && (
          <div className={shell.headActions}>
            <Button
              label="Add closure"
              disabled={!pageOperations?.create}
              onClick={() => setEditing({})}
            />
          </div>
        )}
      </div>
      <ExchangeScopeSelector {...scope} />
    </div>
  );

  // All markets is a valid lens elsewhere, but a closure belongs to exactly
  // one exchange — there is nothing to show or create without one.
  if (!exchangeId)
    return (
      <div className={shell.page}>
        {header}
        <div className={styles.empty}>
          <h3>Pick an exchange</h3>
          <p>
            Closure windows are scheduled per exchange, so there is no
            all-markets view. Choose one above.
          </p>
        </div>
      </div>
    );

  return (
    <div className={shell.page}>
      {header}

      {unionError && (
        <Alert
          className={styles.alert}
          type="error"
          showIcon
          message={unionError.message}
          action={<Button label="Retry" type="secondary" onClick={refetch} />}
        />
      )}

      <div className={styles.metrics}>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>Right now</span>
          <span className={styles.metricValue}>
            {marketOpen == null ? '—' : marketOpen ? 'Open' : 'Closed'}
          </span>
          <span className={styles.metricHint}>
            resolved from the active windows
          </span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>Upcoming</span>
          <span className={styles.metricValue}>{upcomingCount}</span>
          <span className={styles.metricHint}>on this page</span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>Active</span>
          <span className={styles.metricValue}>{activeCount}</span>
          <span className={styles.metricHint}>applied to investors</span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>Total</span>
          <span className={styles.metricValue}>{total}</span>
          <span className={styles.metricHint}>
            on {activeExchange?.code ?? 'this exchange'}
          </span>
        </div>
      </div>

      <div className={styles.listPanel}>
        {loading && !rows.length ? (
          <div className={styles.centered}>
            <Spin size="large" />
          </div>
        ) : sorted.length === 0 ? (
          <div className={styles.empty}>
            <h3>No closure windows</h3>
            <p>
              {activeExchange?.code ?? 'This exchange'} has no scheduled
              closures. Investors can place orders on any day until one is added
              and activated.
            </p>
            <Button
              label="Add closure"
              disabled={!pageOperations?.create}
              onClick={() => setEditing({})}
            />
          </div>
        ) : (
          <>
            <div className={styles.table}>
              <Table
                rowKey="id"
                loading={loading}
                columns={columns}
                dataSource={sorted}
                pagination={false}
                rowClassName={record =>
                  isUpcoming(record) ? '' : styles.pastRow
                }
              />
            </div>
            {total > limit && (
              <div className={styles.tableFoot}>
                <Pagination
                  current={page}
                  pageSize={limit}
                  total={total}
                  showSizeChanger
                  onChange={(nextPage, nextLimit) => {
                    setPage(nextPage);
                    setLimit(nextLimit);
                  }}
                />
              </div>
            )}
          </>
        )}
      </div>

      <ExchangeClosureModal
        open={Boolean(editing)}
        closure={editing?.id ? editing : null}
        exchangeId={exchangeId}
        exchangeCode={activeExchange?.code}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          refetch();
          refetchMarket();
        }}
      />

      <ConfirmModal
        openModal={Boolean(deleting)}
        setOpenModal={() => setDeleting(null)}
        title="Delete closure window"
        loading={deleteLoading}
        handleOperation={handleDelete}
        confirmText={
          <span>
            Permanently delete <strong>{deleting?.title}</strong> on{' '}
            <strong>{moment(deleting?.date).format('D MMMM YYYY')}</strong>?
            This cannot be undone.
          </span>
        }
      />
    </div>
  );
};

const PencilIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true">
    <path
      d="M4 20h4L19 9a2.1 2.1 0 0 0-3-3L5 17v3Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
  </svg>
);

const TrashIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true">
    <path
      d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13M10 11v6M14 11v6"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default ExchangeClosures;
